import { useEffect, useRef } from 'react';
import {
  AreaSeries,
  BarSeries,
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  HistogramSeries,
  LineSeries,
  PriceScaleMode,
  LineStyle,
  createChart,
  createSeriesMarkers,
  type AutoscaleInfo,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  type ISeriesMarkersPluginApi,
  type SeriesMarker,
  type SeriesType,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts';
import type { ChartColors } from './chartColors';
import type { ChartLine, SymbolPrecision } from './chartTrading';
import { LineLayer, type LineLayerHandlers } from './lineLayer';
import { IndicatorLayer } from '../indicators/IndicatorLayer';
import { DrawingLayer, type ActiveTool, type DrawingHandlers } from '../drawings/DrawingLayer';
import type { Drawing } from '../drawings/model';
import type { RenderedIndicator } from '../indicators/instances';
import { PAGE_THRESHOLD, precisionFor, type Bar, type ChartType } from './chartModel';
import { zoomedSpacing } from './toolbar';
import { TradeLinksPrimitive, type TradeLink } from './tradeLinks';
import { SeparatorsPrimitive } from './separators';
import type { ChartProperties } from './chartProperties';

const time = (t: number) => t as UTCTimestamp;

function pointOf(type: ChartType, bar: Bar) {
  return type === 'line' || type === 'area'
    ? { time: time(bar.t), value: bar.c }
    : { time: time(bar.t), open: bar.o, high: bar.h, low: bar.l, close: bar.c };
}

/** What the toolbar can ask of the chart. */
export interface ChartControls {
  /** The chart as a picture (candles, indicators, drawings and trade lines), or null before it is ready. */
  screenshot(): HTMLCanvasElement | null;
  zoom(direction: 1 | -1): void;
  /** Scrolls to the newest bar. */
  toLatest(): void;
  /** Moves the chart one bar to the left, bringing the next bar into view (MT5's F12). */
  step(): void;
}

export interface ChartViewProps {
  bars: Bar[];
  type: ChartType;
  digits: number;
  colors: ChartColors;
  bid: number | null;
  ask: number | null;
  showBidAsk: boolean;
  onNeedOlder: () => void;
  onCrosshair: (bar: Bar | null) => void;
  /** Lines for positions, orders and their stops, and whether the user may drag them right now. */
  lines: ChartLine[];
  editable: boolean;
  precision: SymbolPrecision;
  lineHandlers: LineLayerHandlers;
  indicators: RenderedIndicator[];
  drawings: Drawing[];
  selectedDrawing: string | null;
  drawingTool: ActiveTool | null;
  drawingColor: string;
  /** Drawings snap to the nearest bar and its open, high, low or close. */
  magnet: boolean;
  /** Keep the newest bar in view as new bars arrive. */
  autoScroll: boolean;
  /** Arrows for the account's past trades. */
  markers: SeriesMarker<UTCTimestamp>[];
  /** Lines joining each past trade's entry to its exit. */
  links: TradeLink[];
  /** Colours of the winning and losing links. */
  linkColors: { up: string; down: string };
  /** Receives the chart's controls once it exists (and null when it goes). */
  onControls: (controls: ChartControls | null) => void;
  drawingHandlers: DrawingHandlers;
  /** Length of one bar in seconds, so drawings can run past the last bar. */
  barSeconds: number;
  /** Whether the keyboard (Delete, Escape) belongs to this chart right now. */
  hasKeyboard: () => boolean;
  /** Grid, volumes, scale, shift and the chart's own background (F8). */
  properties: ChartProperties;
  /** A background colour of this chart only; null follows the theme. */
  background: string | null;
  /** Where period separators go (bar times). */
  separators: number[];
}

/**
 * A thin wrapper around TradingView Lightweight Charts. It owns the imperative chart and keeps it in step with props:
 * appending or updating the live bar is an incremental `update`; anything else (new symbol, older page, type change) is a
 * full `setData` that keeps the user's scroll position.
 */
export function ChartView(props: ChartViewProps) {
  const host = useRef<HTMLDivElement>(null);
  const chart = useRef<IChartApi | null>(null);
  const series = useRef<ISeriesApi<SeriesType> | null>(null);
  const lines = useRef<{ bid?: IPriceLine; ask?: IPriceLine }>({});
  const shown = useRef<{ bars: Bar[]; type: ChartType } | null>(null);
  const layer = useRef<LineLayer | null>(null);
  const indicatorLayer = useRef<IndicatorLayer | null>(null);
  const drawingLayer = useRef<DrawingLayer | null>(null);
  const markers = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const tradeLines = useRef<TradeLinksPrimitive | null>(null);
  const separators = useRef<SeparatorsPrimitive | null>(null);
  const volumes = useRef<ISeriesApi<'Histogram'> | null>(null);
  const volumesShown = useRef<Bar[] | null>(null);
  const latest = useRef(props);
  useEffect(() => {
    latest.current = props;
  });

  const sync = () => {
    const s = series.current;
    const c = chart.current;
    if (!s || !c) return;
    const { bars, type } = latest.current;
    const before = shown.current;
    if (before && before.type === type && before.bars === bars) return;
    const canAppend =
      before &&
      before.type === type &&
      before.bars.length > 0 &&
      bars.length >= before.bars.length &&
      bars.length - before.bars.length <= 1 &&
      bars[0]!.t === before.bars[0]!.t;
    if (canAppend) {
      s.update(pointOf(type, bars[bars.length - 1]!) as never);
    } else {
      const range = c.timeScale().getVisibleLogicalRange();
      const olderCount = before && before.bars.length > 0 ? countBefore(bars, before.bars[0]!.t) : 0;
      s.setData(bars.map((b) => pointOf(type, b)) as never);
      if (range && olderCount > 0)
        c.timeScale().setVisibleLogicalRange({ from: range.from + olderCount, to: range.to + olderCount });
      else if (!before || before.bars.length === 0) c.timeScale().scrollToRealTime();
    }
    shown.current = { bars, type };
  };

  // One chart for the life of the component.
  useEffect(() => {
    const el = host.current!;
    const created = createChart(el, {
      autoSize: true,
      layout: { background: { type: ColorType.Solid, color: 'transparent' }, fontFamily: 'inherit' },
      crosshair: { mode: CrosshairMode.Normal },
      timeScale: { timeVisible: true, secondsVisible: false, rightOffset: 6 },
      rightPriceScale: { scaleMargins: { top: 0.1, bottom: 0.1 } },
    });
    chart.current = created;
    // Number of panes (price + indicator windows), for end-to-end tests.
    (el as HTMLElement & { __adioxPanes?: () => number }).__adioxPanes = () => created.panes().length;
    layer.current = new LineLayer(el, created, () => series.current);
    indicatorLayer.current = new IndicatorLayer(created);
    // Created after the trade-line layer, so a grabbed trade line takes the click before a drawing can.
    drawingLayer.current = new DrawingLayer(el, created, () => series.current);
    created.timeScale().subscribeVisibleLogicalRangeChange((range) => {
      if (range && range.from < PAGE_THRESHOLD) latest.current.onNeedOlder();
    });
    latest.current.onControls({
      screenshot: () => created.takeScreenshot(true, false),
      zoom: (direction) => {
        const scale = created.timeScale();
        scale.applyOptions({ barSpacing: zoomedSpacing(scale.options().barSpacing, direction) });
      },
      toLatest: () => created.timeScale().scrollToRealTime(),
      step: () => {
        const scale = created.timeScale();
        scale.scrollToPosition(scale.scrollPosition() + 1, false);
      },
    });
    created.subscribeCrosshairMove((param) => {
      if (param.time === undefined) return latest.current.onCrosshair(null);
      const at = param.time as number;
      const held = shown.current?.bars ?? [];
      let lo = 0;
      let hi = held.length - 1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        const t = held[mid]!.t;
        if (t === at) return latest.current.onCrosshair(held[mid]!);
        if (t < at) lo = mid + 1;
        else hi = mid - 1;
      }
      latest.current.onCrosshair(null);
    });
    return () => {
      latest.current.onControls(null);
      layer.current?.destroy();
      layer.current = null;
      indicatorLayer.current?.destroy();
      indicatorLayer.current = null;
      drawingLayer.current?.destroy();
      drawingLayer.current = null;
      created.remove();
      chart.current = null;
      series.current = null;
      markers.current = null;
      tradeLines.current = null;
      separators.current = null;
      volumes.current = null;
      volumesShown.current = null;
      shown.current = null;
      lines.current = {};
    };
  }, []);

  // Colours and grid follow the theme, or the chart's own colours (F8).
  useEffect(() => {
    const c = props.colors;
    const grid = props.properties.grid;
    chart.current?.applyOptions({
      layout: {
        textColor: c.muted,
        attributionLogo: true,
        background: { type: ColorType.Solid, color: props.background ?? 'transparent' },
      },
      grid: {
        vertLines: {
          color: c.gridDotted ? c.border : c.border + '55',
          style: c.gridDotted ? LineStyle.Dotted : LineStyle.Solid,
          visible: grid,
        },
        horzLines: {
          color: c.gridDotted ? c.border : c.border + '55',
          style: c.gridDotted ? LineStyle.Dotted : LineStyle.Solid,
          visible: grid,
        },
      },
      rightPriceScale: { borderColor: c.border },
      timeScale: { borderColor: c.border },
      crosshair: {
        vertLine: { color: c.muted, labelBackgroundColor: c.accent },
        horzLine: { color: c.muted, labelBackgroundColor: c.accent },
      },
    });
    series.current?.applyOptions(seriesOptions(props.type, c) as never);
  }, [props.colors, props.type, props.properties.grid, props.background]);

  // Chart shift and the price scale (automatic, logarithmic or fixed).
  const { chartShift, scale, fixedMin, fixedMax } = props.properties;
  useEffect(() => {
    chart.current?.timeScale().applyOptions({ rightOffset: chartShift ? 12 : 0 });
  }, [chartShift]);
  useEffect(() => {
    chart.current?.priceScale('right').applyOptions({
      mode: scale === 'log' ? PriceScaleMode.Logarithmic : PriceScaleMode.Normal,
      autoScale: true,
    });
  }, [scale, fixedMin, fixedMax, props.type]);

  // Tick volumes along the bottom of the price window.
  useEffect(() => {
    const c = chart.current;
    if (!c) return;
    if (!props.properties.volumes) {
      if (volumes.current) c.removeSeries(volumes.current);
      volumes.current = null;
      volumesShown.current = null;
      return;
    }
    if (!volumes.current) {
      volumes.current = c.addSeries(HistogramSeries, {
        priceScaleId: 'volume',
        priceLineVisible: false,
        lastValueVisible: false,
        priceFormat: { type: 'volume' },
      });
      c.priceScale('volume').applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
      volumesShown.current = null;
    }
    const { up, down } = props.colors;
    const point = (b: Bar) => ({
      time: b.t as UTCTimestamp,
      value: b.tv,
      color: (b.c >= b.o ? up : down) + '88',
    });
    const before = volumesShown.current;
    const bars = props.bars;
    if (
      before &&
      before.length > 0 &&
      bars.length - before.length <= 1 &&
      bars.length >= before.length &&
      bars[0]?.t === before[0]?.t
    ) {
      if (bars.length > 0) volumes.current.update(point(bars[bars.length - 1]!));
    } else {
      volumes.current.setData(bars.map(point));
    }
    volumesShown.current = bars;
  }, [props.properties.volumes, props.bars, props.colors]);

  // (Re)create the series when the chart type changes.
  useEffect(() => {
    const c = chart.current;
    if (!c) return;
    if (series.current) c.removeSeries(series.current);
    const definition =
      props.type === 'candles'
        ? CandlestickSeries
        : props.type === 'bars'
          ? BarSeries
          : props.type === 'line'
            ? LineSeries
            : AreaSeries;
    series.current = c.addSeries(
      definition as never,
      {
        ...seriesOptions(props.type, latest.current.colors),
        // The price scale also covers the account's lines, so a stop or an order that is far from the candles is still on screen.
        // A fixed scale (F8) shows exactly the range the user set.
        autoscaleInfoProvider: (base: () => AutoscaleInfo | null): AutoscaleInfo | null => {
          const p = latest.current.properties;
          if (p.scale === 'fixed' && p.fixedMin !== null && p.fixedMax !== null) {
            return { priceRange: { minValue: p.fixedMin, maxValue: p.fixedMax } };
          }
          const result = base();
          const extent = layer.current?.extent();
          if (!extent) return result;
          return {
            priceRange: {
              minValue: Math.min(result?.priceRange?.minValue ?? extent.min, extent.min),
              maxValue: Math.max(result?.priceRange?.maxValue ?? extent.max, extent.max),
            },
            margins: result?.margins,
          };
        },
      } as never,
    );
    shown.current = null;
    lines.current = {};
    markers.current = createSeriesMarkers(series.current, [], { zOrder: 'aboveSeries' });
    tradeLines.current = new TradeLinksPrimitive();
    series.current.attachPrimitive(tradeLines.current);
    tradeLines.current.set(latest.current.links, latest.current.linkColors);
    separators.current = new SeparatorsPrimitive();
    series.current.attachPrimitive(separators.current);
    separators.current.set(latest.current.separators, latest.current.colors.muted);
    sync();
    markers.current?.setMarkers(latest.current.markers as never);
  }, [props.type]);

  // Past trades.
  useEffect(() => {
    markers.current?.setMarkers(props.markers as never);
    // How many trade arrows are shown, for end-to-end tests.
    const count = props.markers.length;
    (host.current as (HTMLElement & { __adioxMarkers?: () => number }) | null)!.__adioxMarkers = () => count;
  }, [props.markers, props.type]);

  useEffect(() => {
    tradeLines.current?.set(props.links, props.linkColors);
  }, [props.links, props.linkColors, props.type]);

  useEffect(() => {
    separators.current?.set(props.separators, props.colors.muted);
  }, [props.separators, props.colors.muted, props.type]);

  // Following new bars.
  useEffect(() => {
    chart.current?.timeScale().applyOptions({ shiftVisibleRangeOnNewBar: props.autoScroll });
    if (props.autoScroll) chart.current?.timeScale().scrollToRealTime();
  }, [props.autoScroll]);

  // Data.
  useEffect(() => sync());

  // Price precision follows the symbol.
  useEffect(() => {
    const { precision, minMove } = precisionFor(props.digits);
    series.current?.applyOptions({ priceFormat: { type: 'price', precision, minMove } });
  }, [props.digits, props.type]);

  // Drawings.
  useEffect(() => {
    drawingLayer.current?.update(
      props.drawings,
      props.selectedDrawing,
      props.drawingTool,
      props.drawingColor,
      props.bars,
      props.barSeconds,
      props.precision,
      () => latest.current.hasKeyboard(),
      {
        onChange: (list) => latest.current.drawingHandlers.onChange(list),
        onSelect: (id) => latest.current.drawingHandlers.onSelect(id),
        onToolDone: () => latest.current.drawingHandlers.onToolDone(),
        onHint: (text) => latest.current.drawingHandlers.onHint(text),
      },
      {
        magnet: props.magnet,
        measureColors: {
          up: props.colors.up,
          down: props.colors.down,
          text: props.colors.text,
          panel: props.colors.panel,
        },
      },
    );
  }, [
    props.drawings,
    props.selectedDrawing,
    props.drawingTool,
    props.drawingColor,
    props.magnet,
    props.colors,
    props.bars,
    props.barSeconds,
    props.precision,
    props.type,
  ]);

  // Indicators: on the price pane or in panes below it.
  useEffect(() => {
    indicatorLayer.current?.update(props.indicators, props.colors.muted);
  }, [props.indicators, props.colors.muted]);

  // The account's lines (positions, orders, SL/TP) and dragging them.
  useEffect(() => {
    layer.current?.update(props.lines, props.editable, props.colors, props.precision, {
      onDragMove: (line, price) => latest.current.lineHandlers.onDragMove(line, price),
      onDragEnd: (line, price) => latest.current.lineHandlers.onDragEnd(line, price),
      onContextMenu: (info) => latest.current.lineHandlers.onContextMenu(info),
    });
    chart.current?.priceScale('right').applyOptions({ autoScale: true }); // take the new lines into the scale
  }, [props.lines, props.editable, props.colors, props.precision, props.type]);

  // Bid and ask lines.
  useEffect(() => {
    const s = series.current;
    if (!s) return;
    const { colors, bid, ask, showBidAsk } = latest.current;
    const wanted: [keyof typeof lines.current, number | null, string, string][] = [
      ['bid', showBidAsk ? bid : null, colors.bid ?? colors.down, 'Bid'],
      ['ask', showBidAsk ? ask : null, colors.ask ?? colors.up, 'Ask'],
    ];
    for (const [key, price, color, title] of wanted) {
      const existing = lines.current[key];
      if (price === null) {
        if (existing) s.removePriceLine(existing);
        delete lines.current[key];
      } else if (existing) {
        existing.applyOptions({ price, color });
      } else {
        lines.current[key] = s.createPriceLine({
          price,
          color,
          lineWidth: 1,
          lineStyle: LineStyle.Dashed,
          axisLabelVisible: true,
          title,
        });
      }
    }
  }, [props.bid, props.ask, props.showBidAsk, props.colors, props.type]);

  return <div ref={host} className="h-full w-full" data-testid="chart-canvas" />;
}

/** How many bars in `bars` come before the bar starting at `t` (the bars a scroll-back page added in front). */
function countBefore(bars: Bar[], t: number): number {
  const i = bars.findIndex((b) => b.t === t);
  return i < 0 ? 0 : i;
}

function seriesOptions(type: ChartType, c: ChartColors) {
  switch (type) {
    case 'candles':
      return {
        upColor: c.bullBody ?? c.up,
        downColor: c.bearBody ?? c.down,
        borderUpColor: c.barUp ?? c.up,
        borderDownColor: c.barDown ?? c.down,
        wickUpColor: c.barUp ?? c.up,
        wickDownColor: c.barDown ?? c.down,
        priceLineVisible: false,
      };
    case 'bars':
      return { upColor: c.barUp ?? c.up, downColor: c.barDown ?? c.down, priceLineVisible: false };
    case 'line':
      return { color: c.line ?? c.accent, lineWidth: c.line ? 1 : 2, priceLineVisible: false };
    case 'area':
      return {
        lineColor: c.accent,
        topColor: c.accent + '66',
        bottomColor: c.accent + '05',
        lineWidth: 2,
        priceLineVisible: false,
      };
  }
}
